import type { CaptureNode, RoomCaptureCommand } from "./captureClient";

export const roomCaptureState = (
  nodes: CaptureNode[] | undefined,
  roomDesired: { enabled?: boolean | null; revision?: number },
  roomUuid: string,
  command?: RoomCaptureCommand,
) => {
  const rosterLoaded = nodes !== undefined;
  const knownNodes = nodes ?? [];
  const nodeDesired = knownNodes.reduce<{
    enabled: boolean | null;
    revision: number;
  } | null>((latest, node) => {
    if (node.desiredEnabled === null) return latest;
    if (latest && latest.revision > node.desiredRevision) return latest;
    return { enabled: node.desiredEnabled, revision: node.desiredRevision };
  }, null);
  const serverDesired = nodeDesired ?? {
    enabled: roomDesired.enabled ?? null,
    revision: roomDesired.revision ?? 0,
  };
  const desired =
    command?.roomUuid === roomUuid && command.revision > serverDesired.revision
      ? { enabled: command.enabled, revision: command.revision }
      : serverDesired;
  const appliedEnabled = knownNodes.some((node) => node.captureEnabled === true)
    ? true
    : knownNodes.length > 0 &&
        knownNodes.every((node) => node.captureEnabled === false)
      ? false
      : null;
  const enabled = desired.enabled ?? appliedEnabled ?? true;
  // Only nodes that can ack a command; an offline node would hold `pending` forever.
  const reachableNodes = knownNodes.filter(
    (node) => node.connectorLive && node.controlConnected,
  );
  const pending =
    rosterLoaded &&
    desired.revision > 0 &&
    (knownNodes.length === 0 ||
      reachableNodes.some(
        (node) =>
          node.captureRevision < desired.revision ||
          node.captureEnabled !== enabled,
      ));

  return {
    enabled,
    /** Device-reported: true if any node captures, false only once every node reports off. */
    applied: appliedEnabled,
    revision: desired.revision,
    pending,
    failed: knownNodes.some(
      (node) =>
        node.controlError !== null && node.captureRevision >= desired.revision,
    ),
    unavailable: rosterLoaded && reachableNodes.length === 0,
  };
};
